import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import CustomTextField from 'src/components/forms/theme-elements/CustomTextField';
import { FormikProps } from 'formik';
import CustomCheckbox from 'src/components/forms/theme-elements/CustomCheckbox';
import { t } from 'i18next';
import { useSearchUsersMutation } from 'src/services/tournaments';
import { Organizer } from 'src/services/tournaments/searchUsers';
import { debounce } from 'lodash';
import { useCallback } from 'react';
import { useLazySearchFederationUsersQuery } from 'src/services/federation';
import { usePermissions } from 'src/hooks/usePermissions';
import { FederationsPermissions } from 'src/types/apps/permissions';
import { useSearchForUsersMutation } from 'src/services/users';

interface SearchState {
  users: Organizer[];
  page: number;
  hasMore: boolean;
  searchTerm: string;
}

export default function SearchUserInput({
  formik,
}: {
  formik: FormikProps<{ users_ids: number[]; users: Organizer[] }>;
}) {
  const [searchState, setSearchState] = React.useState<SearchState>({
    users: [],
    page: 1,
    hasMore: true,
    searchTerm: '',
  });

  const [isSearching, setIsSearching] = React.useState(false);

  const [searchUsers]: any = useSearchForUsersMutation();
  const [searchFederationUsers] = useLazySearchFederationUsersQuery();

  const currentSearchRef = React.useRef('');

  const isFederation = usePermissions(FederationsPermissions.TOURNAMENTS_VIEW);

  const availableOptions = React.useMemo(() => {
    if (searchState.searchTerm?.trim()?.length <= 2) return [];

    return searchState.users;
  }, [searchState.users, searchState.searchTerm]);

  const debouncedSearch = useCallback(
    debounce(async (searchTerm: string, page: number, isNewSearch: boolean) => {
      if (searchTerm?.trim()?.length <= 2) return;

      if (searchTerm !== currentSearchRef.current && isNewSearch) {
        return;
      }

      try {
        setIsSearching(true);

        const searchKey = /[a-zA-Z]/.test(searchTerm) ? 'name' : 'phone';

        const result = isFederation
          ? await searchFederationUsers({ search: searchTerm, page }).unwrap()
          : await searchUsers({ search: searchTerm, page, searchKey }).unwrap();

        if (result?.data) {
          if (searchTerm === currentSearchRef.current || !isNewSearch) {
            setSearchState((prev) => ({
              ...prev,
              users: isNewSearch ? result.data : [...prev.users, ...result.data],
              hasMore: result?.data?.length > 0,
              page: page,
            }));
          }
        }
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [searchUsers],
  );

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (value: string) => {
    const newSearchTerm = value.trim();

    currentSearchRef.current = newSearchTerm;

    debouncedSearch.cancel();

    setSearchState({
      users: [],
      page: 1,
      hasMore: true,
      searchTerm: newSearchTerm,
    });

    if (newSearchTerm?.length > 2) {
      debouncedSearch(newSearchTerm, 1, true);
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget;
    if (
      !isSearching &&
      searchState.hasMore &&
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 100
    ) {
      debouncedSearch(searchState.searchTerm, searchState.page + 1, false);
    }
  };

  return (
    <Autocomplete
      multiple
      id="users-search-autocomplete"
      value={formik.values.users}
      options={availableOptions}
      disableCloseOnSelect
      filterOptions={(x) => x}
      includeInputInList
      noOptionsText={
        searchState.searchTerm?.trim()?.length > 2
          ? t('CouponsScreen.noUsers')
          : t('CouponsScreen.enterAtLeast3Chars')
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={isSearching && searchState.page === 1}
      onChange={(_, newValue) => {
        formik.setFieldValue('users', newValue);
        formik.setFieldValue(
          'users_ids',
          newValue.map((user) => user.id),
        );
        formik.validateField('users');
      }}
      getOptionLabel={(option) => option.name}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <CustomCheckbox style={{ marginRight: 8 }} checked={selected} />
          {`${option.name} - ID: ${option.id}`}
        </li>
      )}
      fullWidth
      renderInput={(params) => (
        <CustomTextField
          {...params}
          placeholder={t('TournamentOrganizers.searchWithName')}
          error={formik.touched.users && Boolean(formik.errors.users)}
          helperText={formik.touched.users && formik.errors.users}
          onChange={(e) => handleSearchChange(e.target.value)}
          onBlur={() => {
            formik.validateField('users');
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isSearching && searchState.page === 1 ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      ListboxProps={{
        onScroll: handleScroll,
      }}
    />
  );
}
